import { addLog } from 'assets/components/feedback/Feedback';
import useForm from 'assets/components/form/hooks/Form';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import ProviderBuilder from 'assets/providers/ProviderBuilder';
import { date, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { createPathFromRoute } from 'assets/utils/dom/UrlParsing';
import { isEmail, isValidPhoneNumber } from 'assets/utils/parsersAndValidation/Validators';
import { HttpStatus } from 'config/Api.Config';
import { first, flatten, groupBy, isEmpty, set, sortBy } from 'lodash';
import claimSavePublic from 'models/claims/claim/savePublic/Api.Claim.SavePublic';
import claimDocumentSavePublicApi from 'models/claims/claimDocument/savePublic/Api.ClaimDocument.SavePublic';
import claimJobDocumentSavePublicApi from 'models/claims/claimJobDocument/savePublic/Api.ClaimJobDocument.SavePublic';
import ClaimJobItemPayee from 'models/claims/claimJobItemPayee/Model.ClaimJobItemPayee';
import ClaimType from 'models/claims/claimType/Model.ClaimType';
import Payee from 'models/claims/payee/Model.Payee';
import payeeSavePublic from 'models/claims/payee/savePublic/Api.Payee.SavePublic';
import PayeeType from 'models/claims/payeeType/Model.PayeeType';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import { ClaimCreatorOptions } from 'models/enums/ClaimCreation';
import ClaimJobStatusCode from 'models/enums/ClaimJobStatusCode';
import ClaimStatusCode from 'models/enums/ClaimStatusCode';
import ClaimTypeDataPointInputType from 'models/enums/ClaimTypeDataPointInputType';
import Currency from 'models/enums/Currency';
import LineItem from 'models/enums/LineItem';
import PaymentStatus from 'models/enums/PaymentStatus';
import ProductType from 'models/enums/ProductType';
import Insurer from 'models/productManagement/insurer/Model.Insurer';
import importLegacyContractApi from 'models/sales/contract/legacy/importLegacyContract/Api.ImportLegacyContract';
import { mapLegacyContractStatusForImport } from 'module/claims/helper/Claim.ContractEligibility';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { publicClaimSubmitIndexRoute } from './Claim.Submit.Index';
import { publicClaimResponsePageIndexRoute } from './components/Claim.Submit.ResponsePage';

export enum PublicClaimSubmitActions {
  CREATE_REPAIR_FACILITY = 'createRepairFacility',
}
export function usePublicClaimPageRouter() {
  return usePageRouter<undefined, Module.Claims.Claim.Submit.FormQuery>({ route: publicClaimSubmitIndexRoute });
}
export type ClaimJobProps = {
  claimJob: Utils.FormData<Module.Claims.Claim.Submit.ClaimJobForUpdate>;
  index: number;
};
export type PublicClaimJobItem = Utils.FormData<
  Utils.Unpacked<Module.Claims.Claim.Submit.ClaimJobForUpdate['claimJobItems']>
>;
export default function usePublicClaimSubmit() {
  const { lang } = getLocales();
  const _history = useHistory();

  const repairFacilityListApi = useApi({
    action: Payee.list,
    body: { payeeTypeId: [PayeeType.REPAIR_FACILITY_ID], orderBy: 'name', orderType: 'asc' },
  });

  const form = useForm<Module.Claims.Claim.Submit.Form & { uploadableClaimTypeDocuments: Model.IClaimTypeDocument[] }>({
    default: {
      claim: {
        submitedDateUtc: date().tz('UTC').format(defaultServerDateTimeFormat),
        statusCode: ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_OTHER,
        isVehicleInoperable: false,
        createdByPayee: {
          name: undefined,
          email: undefined,
          phoneNumber: undefined,
          payeeTypeId: PayeeType.OTHER_ID,
        },
        claimJobs: [
          {
            claimTypeId: null,
            cause: undefined,
            customerComplaint: undefined,
            correction: undefined,
            claimJobDocuments: [],
            dataPoints: [],
            claimJobItems: [],
            statusCode: ClaimJobStatusCode.OPEN_ACTIVE_IN_PROGRESS,
          },
        ],
      },
      newPayee: null,
      claimDocuments: [],
    },
    onSubmit: async (data) => {
      if (!data.claim?.contract?.id) {
        const contractForImport = mapLegacyContractStatusForImport(data.claim?.contract);
        const res = await importLegacyContractApi(contractForImport, true);

        if (!res.payload?.id) {
          addLog({ error: res.message });
          return;
        }

        data.claim.contractId = res.payload.id;
      }

      if (data.newPayee) {
        const payeeResponse = await payeeSavePublic(data.newPayee);
        if (payeeResponse.status !== HttpStatus.ok) {
          addLog({ error: payeeResponse.message });
          return;
        }
      }

      const finalClaimJobs = data?.claim?.claimJobs?.map((it, idx) => ({
        ...it,
        jobNumber: idx + 1,
        statusCode: ClaimJobStatusCode.OPEN_ACTIVE_IN_PROGRESS,
        claimJobItems: it?.claimJobItems?.length
          ? it?.claimJobItems?.map((ji) => ({
              ...ji,
              provincialSalesTax: ji.jobItemTypeCode === LineItem.deductible ? 0 : ji.provincialSalesTax,
              gstHst: ji.jobItemTypeCode === LineItem.deductible ? 0 : ji.gstHst,
            }))
          : [],
        claimJobDocuments: it?.claimJobDocuments?.map((doc) => ({ ...doc, jobNumber: idx + 1 })),
      }));
      const claimJobDocuments = flatten(finalClaimJobs?.map((it) => it.claimJobDocuments) || []);

      const claimResponse = await claimSavePublic({
        body: {
          ...data.claim,
          contactName: `${data.claim?.contract?.client?.firstName} ${data.claim?.contract?.client?.lastName}`,
          contactEmail: data.claim?.contract?.client?.email,
          contactPhone: data.claim?.contract?.client?.phone,
          streetAddress: data.claim?.contract?.client?.streetAddress,
          city: data.claim?.contract?.client?.city,
          postalCode: data.claim?.contract?.client?.postalCode,
          countryCode: data.claim?.contract?.client?.countryCode,
          provinceCode: data.claim?.contract?.client?.provinceCode,
          odometerReading: data.claim.odometerReading ? data.claim.odometerReading : 0,
          claimJobs: finalClaimJobs,
        },
        query: {
          lastName: data.claim.contract?.client?.lastName,
          vin: data.claim.contract?.vehicle?.vin,
          contractNumber: data.claim.contract?.appNumber,
        },
      });

      const newClaim = claimResponse.payload;
      if (newClaim?.id && claimResponse.status === HttpStatus.ok) {
        if (!isEmpty(data.claimDocuments)) {
          for (const document of data.claimDocuments) {
            document.claimId = newClaim?.id;
            await claimDocumentSavePublicApi({
              body: document,
              params: {
                clientName: data.claim.contract?.client?.lastName,
                vin: data.claim.contract?.vehicle?.vin,
                contractNumber: data.claim.contract?.appNumber,
                id: newClaim?.id?.toString(),
              },
            });
          }
        }

        if (!isEmpty(claimJobDocuments) && !isEmpty(newClaim?.claimJobs)) {
          for (const claimJobDoc of claimJobDocuments) {
            const claimJob = newClaim?.claimJobs?.find((it) => it?.jobNumber === claimJobDoc?.jobNumber);
            if (claimJobDoc && claimJob)
              await claimJobDocumentSavePublicApi({
                body: {
                  ...claimJobDoc,
                  formFile: claimJobDoc.formFile,
                  name: claimJobDoc.name,
                  notes: claimJobDoc.notes,
                  claimJobId: claimJob.id,
                },
                params: {
                  claimJobId: claimJob.id,
                },
              });
          }
        }

        addLog({ success: claimResponse.message || lang.saveSuccess });
      } else {
        addLog({ error: claimResponse.message || lang.saveError });
      }
      if (newClaim && !document.location.pathname.startsWith('/public/claim/createclaim'))
        _history.push(createPathFromRoute(publicClaimResponsePageIndexRoute, { claimNumber: newClaim?.number }));
    },
    validation: (data, errors) => {
      for (const claimJob of data?.claim?.claimJobs ?? []) {
        const hasNoDocuments = data?.uploadableClaimTypeDocuments?.some(
          (ud) =>
            ud?.claimTypeId === claimJob?.claimTypeId &&
            ud?.required &&
            !claimJob?.claimJobDocuments?.some((cjd) => cjd.claimTypeDocumentId === ud?.id)
        );

        const hasMissingDataPoints = claimJob?.dataPoints?.some(
          (it) => it.type !== ClaimTypeDataPointInputType.Boolean && it.isRequired && !it.value
        );

        const hasInvalidItems =
          !claimJob?.claimTypeId ||
          claimJob?.claimJobItems?.some((item) =>
            item.jobItemTypeCode !== LineItem.deductible
              ? !item.lossCode || (item.gstHst ?? 0) > 1 || (item.provincialSalesTax ?? 0) > 1
              : (item.gstHst ?? 0) > 1 || (item.provincialSalesTax ?? 0) > 1
          );

        if (hasNoDocuments) {
          set(errors, `claim.claimJobs._objectError`, lang.noDocumentsUploaded);
        }
        if (hasMissingDataPoints) {
          set(errors, `claim.claimJobs._objectError`, lang.noDetailsSpecified);
        }
        if (hasInvalidItems) {
          set(errors, `claim.claimJobs._objectError`, lang.invalidJobItems);
        }
      }

      if (!data.claimDocuments) set(errors, 'claimDocuments._objectError', lang.required);
      if (!data.claim?.contract) set(errors, 'claim.contract._objectError', lang.required);
      if (!data.claimCreationOption) set(errors, 'claim.claimCreationOption', lang.required);

      // Payee as new repair facility validation
      if (!data.repairFacilityId && data?.claimCreationOption === ClaimCreatorOptions.REPAIR_FACILITY && !data.newPayee)
        set(errors, 'repairFacilityId', lang.required);
      if (data?.claimCreationOption === ClaimCreatorOptions.REPAIR_FACILITY && data.newPayee) {
        if (!data.newPayee?.name) set(errors, 'newPayee.name', lang.required);
        if (!data.newPayee?.email) set(errors, 'newPayee.email', lang.required);
        else if (repairFacilityListApi.payload?.data?.some((it) => it.email === data.newPayee?.email))
          set(errors, 'newPayee.email', lang.alreadyExists);
        if (!isEmail(data.newPayee?.email)) set(errors, 'newPayee.email', lang.invalidEmailFormatRequires);
        // TODO: See if this is required???
        // if (!data.newPayee?.phoneNumber) set(errors, 'newPayee.phoneNumber', lang.required);
        if (data.newPayee?.phoneNumber && !isValidPhoneNumber(data.newPayee?.phoneNumber))
          set(errors, 'newPayee.phoneNumber', lang.invalidFormatRequires.replace('{format}', '###-###-####'));
        if (!data.newPayee?.countryCode) set(errors, 'newPayee.countryCode', lang.required);
      }
      //

      if (data.claimCreationOption === ClaimCreatorOptions.DEALERSHIP && !data.claim?.dealerId)
        set(errors, 'claim.dealerId', lang.required);
      if (!data?.claimCreationOption) set(errors, 'claimCreationOption', lang.required);
      if (!data.claim?.createdByPayee?.email) set(errors, 'claim.createdByPayee.email', lang.required);
      if (!isEmail(data.claim?.createdByPayee?.email))
        set(errors, 'claim.createdByPayee.email', lang.invalidEmailFormatRequires);
      if (!data.claim?.createdByPayee?.name) set(errors, 'claim.createdByPayee.name', lang.required);
      if (!data.claim?.createdByPayee?.phoneNumber) set(errors, 'claim.createdByPayee.phoneNumber', lang.required);
      if (!data.claim?.createdByPayee?.email) set(errors, 'claim.createdByPayee.email', lang.required);
      if (data.claim?.createdByPayee?.phoneNumber && !isValidPhoneNumber(data.claim?.createdByPayee?.phoneNumber))
        set(errors, 'claim.createdByPayee.phoneNumber', lang.invalidFormatRequires.replace('{format}', '###-###-####'));
    },
    beforeUpdate: (data) => {
      if (ClaimCreatorOptions.DEALERSHIP === data.claimCreationOption) {
        data.claim.statusCode = ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_DEALERSHIP;
        data.claim.createdByPayee.payeeTypeId = PayeeType.DEALER_ID;
      } else if (ClaimCreatorOptions.REPAIR_FACILITY === data.claimCreationOption) {
        const payee = repairFacilityListApi?.payload?.data?.find((it) => it.id === data?.repairFacilityId);

        data.claim.statusCode = ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_REPAIRFACILITY;
        data.claim.createdByPayee.payeeTypeId = PayeeType.REPAIR_FACILITY_ID;
        data.claim.createdByPayee.email = data.claim.createdByPayee.email ?? payee?.email;
        data.claim.createdByPayee.phoneNumber = data.claim.createdByPayee.phoneNumber ?? payee?.phoneNumber;
        data.claim.createdByPayee.name = data.claim.createdByPayee.name ?? payee?.name;
      } else if (ClaimCreatorOptions.OTHER === data.claimCreationOption) {
        data.claim.statusCode = ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_OTHER;
        data.claim.createdByPayee.payeeTypeId = PayeeType.OTHER_ID;
      } else if (ClaimCreatorOptions.CUSTOMER === data.claimCreationOption) {
        data.claim.statusCode = ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_CUSTOMER;
        data.claim.createdByPayee.payeeTypeId = PayeeType.CLIENT_ID;
      } else {
        data.claim.createdByPayee.email = undefined;
        data.claim.createdByPayee.phoneNumber = undefined;
        data.claim.createdByPayee.name = undefined;
      }

      return data;
    },
  });

  //#region APIS
  const dealerListPublicApi = useApi({
    action: Dealer.listPublic,
    wait: form.data.claimCreationOption === ClaimCreatorOptions.DEALERSHIP,
    default: {
      orderBy: 'name',
      orderType: 'asc',
    },
  });
  //#endregion

  useEffect(() => {
    if (form.data.claim?.contract?.dealer?.distributorId)
      dealerListPublicApi.execute({
        distributorId: [form.data.claim?.contract?.dealer?.distributorId],
        orderBy: 'name',
        orderType: 'asc',
      });
  }, [form.data.claim?.contract?.dealer?.distributorId]);

  const selectedDealer = useMemo(
    () => dealerListPublicApi?.payload?.data?.find((it) => it.id === form.data.claim?.dealerId),
    [dealerListPublicApi.payload?.data, form.data.claim?.dealerId]
  );
  //#region Claim Types
  const claimTypesListPublicApi = useApi({
    action: ClaimType.listPublic,
    body: {
      productId: [form.data?.claim?.contract?.productId],
    },
    wait: !form.data?.claim?.contract?.productId,
  });
  useEffect(() => {
    if (form.data.claim?.contract?.productId)
      claimTypesListPublicApi.execute({
        productId: [form.data.claim?.contract?.productId],
      });
  }, [form.data.claim?.contract?.productId]);

  const uploadableDocuments = useMemo(() => {
    const docs = flatten(claimTypesListPublicApi?.payload?.data?.map((it) => it.claimTypeDocuments)) ?? [];
    form.update({ uploadableClaimTypeDocuments: docs });
    return docs;
  }, [claimTypesListPublicApi?.payload?.data]);
  //#endregion

  //#region Insurers
  const insurerListPublicApi = useApi({
    action: Insurer.listPublic,
    body: {
      productId: [form.data.claim?.contract?.productId],
    },
    wait: !form.data.claim?.contract?.productId,
  });
  useEffect(() => {
    if (form.data.claim?.contract?.productId)
      insurerListPublicApi.execute({
        productId: [form.data.claim?.contract?.productId],
      });
  }, [form.data.claim?.contract?.productId]);
  //#endregion

  //#region Claim job
  const [isEditingTax, setIsEditingTax] = useState<boolean>(false);
  useEffect(() => {
    if (isEditingTax) addLog({ warning: lang.claimJobItemTaxEditWarning });
  }, [isEditingTax]);

  function updateClaimJob(claimJob: Utils.FormData<Model.IClaimJob>, index: number) {
    const currentJobs = [...form.data.claim.claimJobs];
    currentJobs[index] = { ...claimJob };

    form.merge({
      claim: {
        claimJobs: currentJobs,
      },
    });
  }

  const isWarrantyProduct = form.data.claim?.contract?.product?.productType === ProductType.warrantiClassBased;
  const updateClaimJobClaimType = (updatedJob: Utils.FormData<Model.IClaimJob>, claimJobIndex: number) => {
    const currentClaimTypeId = updatedJob?.claimTypeId;
    const previousClaimTypeId = form.data?.claim?.claimJobs[claimJobIndex]?.claimTypeId;

    if (previousClaimTypeId === currentClaimTypeId) return;

    const claimJobs = [...(form.data.claim.claimJobs ?? [])];
    const claimType = claimTypesListPublicApi?.payload?.data?.find((ct) => ct.id === currentClaimTypeId);

    const newJob: Utils.FormData<Model.IClaimJob> = {
      ...updatedJob,
      claimTypeVersion: claimType?.version,
      dataPoints: sortBy(
        claimType?.claimTypeDataPoints?.map((dp) => ({
          claimTypeDataPointId: dp.id,
          claimTypeId: dp.claimTypeId,
          name: dp.name,
          type: dp.type,
          version: dp.version,
          isRequired: dp.isRequired,
          value: undefined,
        })) ?? [],
        (it) => it.claimTypeDataPointId
      ),
      claimJobItems: [],
      claimJobDocuments: [],
    };

    if (ClaimCreatorOptions.DEALERSHIP === form.data.claimCreationOption && form.data?.claim?.dealerId) {
      const currency = 'CAD';
      const insurerAdjudications = Insurer.createInsurerAdjudications(
        insurerListPublicApi?.payload?.data?.map((it) => new Insurer(it)),
        currency
      );
      const newClaimJobItemPayee = new ClaimJobItemPayee({ dealer: selectedDealer, dealerId: selectedDealer.id });
      const tax = claimType?.products
        ?.find((it) => it.id === form.data.claim?.contract?.productId)
        ?.productTaxes?.find((it) => it.provinceCode === selectedDealer?.provinceCode);
      const paymentMethodCode = selectedDealer?.paymentMethodCode;

      if (!isEmpty(claimType?.claimTypeJobItemTemplates)) {
        newJob.claimJobItems =
          claimType?.claimTypeJobItemTemplates?.map((it) => ({
            templateId: it.id,
            lossCodes: it.lossCodes,
            claimJobItemPayee: newClaimJobItemPayee,
            jobItemTypeCode: it.jobItemTypeCode,
            adjudication: it.correction,
            lossCode: it.lossCodes.length === 1 ? first(it.lossCodes)?.code : undefined,
            gstHst: tax?.claimsGstHst || 0.0,
            provincialSalesTax: tax?.claimsProvincialSalesTax || 0.0,
            total: it.correction + it.correction * ((tax?.claimsGstHst ?? 0) + (tax?.claimsProvincialSalesTax ?? 0)),
            statusCode: PaymentStatus.unpaid,
            baseCost: 0,
            quantity: it.lossCodes?.length === 1 && it.jobItemTypeCode === LineItem.part ? 1 : 0,
            hoursWorked: 0,
            correction: it.correction,
            labourRate: it.jobItemTypeCode === LineItem.labour ? selectedDealer?.labourRate : 0.0,
            paymentMethodCode: paymentMethodCode,
            isCreatedFromTemplate: true,
            insurerAdjudications: insurerAdjudications,
            currency: currency,
          })) ?? [];

        if (isWarrantyProduct) {
          const deductibleCorrection = form.data.claim?.contract?.deductible ?? 0.0;
          newJob.claimJobItems.push({
            claimJobItemPayee: newClaimJobItemPayee,
            jobItemTypeCode: LineItem.deductible,
            adjudication: deductibleCorrection,
            correction: deductibleCorrection,
            gstHst: 0.0,
            provincialSalesTax: 0.0,
            statusCode: PaymentStatus.unpaid,
            total: deductibleCorrection,
            paymentMethodCode: paymentMethodCode,
            isCreatedFromTemplate: true,
            insurerAdjudications: insurerAdjudications,
            currency: currency,
          } as PublicClaimJobItem);
        }
      }

      if (claimType?.allowManualJobItemsCreation) {
        const createJobItem = (
          jobItemTypeCode: LineItem,
          correction: number,
          insurerAdjudications: Model.IInsurerAdjudication
        ) => {
          return {
            claimJobItemPayee: newClaimJobItemPayee,
            jobItemTypeCode,
            statusCode: PaymentStatus.unpaid,
            currency: Currency.CAD,
            adjudication: jobItemTypeCode === LineItem.deductible ? correction : 0.0,
            paymentMethodCode: paymentMethodCode,
            insurerAdjudications: insurerAdjudications,
            correction: correction,
            gstHst: tax?.claimsGstHst,
            provincialSalesTax: tax?.claimsProvincialSalesTax,
            total:
              jobItemTypeCode === LineItem.deductible
                ? correction
                : correction + correction * ((tax?.claimsGstHst ?? 0) + (tax?.claimsProvincialSalesTax ?? 0)),
            baseCost: 0,
            quantity: jobItemTypeCode === LineItem.part ? 1 : 0,
            hoursWorked: jobItemTypeCode === LineItem.labour ? 1 : 0,
            labourRate: jobItemTypeCode === LineItem.labour ? selectedDealer?.labourRate : 0.0,
            isCreatedFromTemplate: false,
            lossCodes: claimType?.lossCodes ?? [],
          } as PublicClaimJobItem;
        };

        if (isWarrantyProduct) {
          newJob.claimJobItems.push(
            createJobItem(LineItem.deductible, form.data?.claim?.contract?.deductible, insurerAdjudications)
          );
        }

        newJob.claimJobItems.push(createJobItem(LineItem.labour, 0.0, insurerAdjudications));
        newJob.claimJobItems.push(createJobItem(LineItem.part, 0.0, insurerAdjudications));
        newJob.claimJobItems.push(createJobItem(LineItem.other, 0.0, insurerAdjudications));
      }
    }

    claimJobs[claimJobIndex] = newJob;
    form.update({ claim: { ...form.data.claim, claimJobs } });
  };

  function addNewClaimJob() {
    form.merge({
      claim: {
        claimJobs: [
          ...(form.data.claim.claimJobs ?? []),
          {
            claimTypeId: null,
            cause: undefined,
            customerComplaint: undefined,
            correction: undefined,
            claimJobDocuments: [],
            dataPoints: [],
            claimJobItems: [],
            statusCode: ClaimJobStatusCode.OPEN_ACTIVE_IN_PROGRESS,
          },
        ],
      },
    });
  }
  const deleteClaimJob = (claimJobIndex: number) => {
    const updatedClaimJobs = form.data?.claim?.claimJobs?.filter((_, idx) => idx !== claimJobIndex);
    form.update({ claim: { ...form.data.claim, claimJobs: updatedClaimJobs } });
  };
  //#endregion
  const shouldShowClaimJobs = (() => {
    if (!form.data.claimCreationOption) return false;
    if (form.data.claimCreationOption === ClaimCreatorOptions.REPAIR_FACILITY)
      return !!(form.data.repairFacilityId || form.data.newPayee);
    if (form.data.claimCreationOption === ClaimCreatorOptions.DEALERSHIP) return !!form.data.claim?.dealerId;
    return true;
  })();

  const updateDetail = (claimJobIndex: number, value: string, id: number) => {
    const claimJobs = form.data?.claim?.claimJobs || [];
    const updatedJobs = [...claimJobs];
    const currentJob = { ...updatedJobs[claimJobIndex] };
    currentJob.dataPoints = currentJob.dataPoints?.map((item) =>
      item.claimTypeDataPointId === id ? { ...item, value } : item
    );
    updatedJobs[claimJobIndex] = currentJob;
    updateClaimJob(currentJob, claimJobIndex);
  };

  function deleteJobItem(claimJobIndex: number, typeCode: string, itemIndex: number) {
    const claimJobs = [...(form.data?.claim?.claimJobs ?? [])];
    const claimJob = claimJobs[claimJobIndex];

    if (!claimJob) return;

    const groupedClaimJobItems = groupBy(claimJob.claimJobItems || [], (it) => it.jobItemTypeCode);

    const targetItems = groupedClaimJobItems[typeCode] || [];
    targetItems.splice(itemIndex, 1);

    claimJob.claimJobItems = Object.values(groupedClaimJobItems).flat();
    claimJobs[claimJobIndex] = claimJob;

    updateClaimJob(claimJob, claimJobIndex);
  }
  function addJobItem(claimJobIndex: number, jobItemTypeCode: string) {
    const claimJobs = [...(form.data?.claim?.claimJobs ?? [])];
    const claimJob = claimJobs[claimJobIndex];

    if (!claimJob) return;

    const claimType = claimTypesListPublicApi?.payload?.data?.find((ct) => ct.id === claimJob?.claimTypeId);
    const currency = Currency.CAD;
    const insurerAdjudications = Insurer.createInsurerAdjudications(insurerListPublicApi?.payload?.data, currency);
    const tax = claimType?.products
      ?.find((it) => it.id === form.data?.claim?.contract?.productId)
      ?.productTaxes?.find((it) => it.provinceCode === selectedDealer?.provinceCode);
    const labourRate = selectedDealer?.labourRate ?? form.data?.claim?.dealer?.labourRate;
    const newClaimJobItemPayee = {
      dealer: selectedDealer ?? form.data?.claim?.dealer,
      dealerId: selectedDealer?.id ?? form.data?.claim?.dealerId,
    };
    const paymentMethodCode = selectedDealer?.paymentMethodCode ?? form.data?.claim?.dealer?.paymentMethodCode;

    const newItem = {
      claimJobItemPayee: new ClaimJobItemPayee(newClaimJobItemPayee),
      jobItemTypeCode,
      insurerAdjudications,
      lossCodes: claimType?.lossCodes ?? [],
      currency,
      gstHst: tax?.claimsGstHst || 0.0,
      provincialSalesTax: tax?.claimsProvincialSalesTax || 0.0,
      correction: 0,
      adjudication: 0,
      statusCode: PaymentStatus.unpaid,
      baseCost: 0,
      quantity: jobItemTypeCode === LineItem.part ? 1 : 0,
      hoursWorked: jobItemTypeCode === LineItem.labour ? 1 : 0,
      labourRate: jobItemTypeCode === LineItem.labour ? labourRate : 0.0,
      paymentMethodCode,
      isCreatedFromTemplate: false,
    };

    if (jobItemTypeCode === LineItem.deductible) {
      newItem.gstHst = 0.0;
      newItem.provincialSalesTax = 0.0;
    }

    claimJob.claimJobItems = [...(claimJob.claimJobItems ?? []), newItem as any];
    claimJobs[claimJobIndex] = claimJob;

    updateClaimJob(claimJob, claimJobIndex);
  }
  function createDefaultJobItems(claimJobIndex: number) {
    const claimJobItems: Array<PublicClaimJobItem> = [];

    const claimJobs = [...(form.data?.claim?.claimJobs ?? [])];
    const claimJob = claimJobs[claimJobIndex];

    if (!claimJob) return;

    const claimType = claimTypesListPublicApi?.payload?.data?.find((ct) => ct.id === claimJob?.claimTypeId);
    const currency = Currency.CAD;
    const insurerAdjudications = Insurer.createInsurerAdjudications(insurerListPublicApi?.payload?.data, currency);
    const tax = claimType?.products
      ?.find((it) => it.id === form.data?.claim?.contract?.productId)
      ?.productTaxes?.find((it) => it.provinceCode === selectedDealer?.provinceCode);
    const labourRate = selectedDealer?.labourRate ?? form.data?.claim?.dealer?.labourRate;
    const newClaimJobItemPayee = {
      dealer: selectedDealer ?? form.data?.claim?.dealer,
      dealerId: selectedDealer?.id ?? form.data?.claim?.dealerId,
    };
    const paymentMethodCode = selectedDealer?.paymentMethodCode ?? form.data?.claim?.dealer?.paymentMethodCode;

    const createJobItem = (
      jobItemTypeCode: LineItem,
      correction: number,
      insurerAdjudications: Model.IInsurerAdjudication
    ) => {
      return {
        claimJobItemPayee: new ClaimJobItemPayee(newClaimJobItemPayee),
        jobItemTypeCode,
        statusCode: PaymentStatus.unpaid,
        currency: Currency.CAD,
        adjudication: 0.0,
        paymentMethodCode: paymentMethodCode,
        insurerAdjudications: insurerAdjudications,
        correction: correction,
        gstHst: tax?.claimsGstHst,
        provincialSalesTax: tax?.claimsProvincialSalesTax,
        baseCost: 0,
        quantity: jobItemTypeCode === LineItem.part ? 1 : 0,
        hoursWorked: jobItemTypeCode === LineItem.labour ? 1 : 0,
        labourRate: jobItemTypeCode === LineItem.labour ? labourRate : 0.0,
        isCreatedFromTemplate: false,
        lossCodes: claimType?.lossCodes ?? [],
      } as PublicClaimJobItem;
    };

    if (isWarrantyProduct) {
      claimJobItems.push(
        createJobItem(LineItem.deductible, form.data?.claim?.contract?.deductible, insurerAdjudications)
      );
    }

    claimJobItems.push(createJobItem(LineItem.labour, 0.0, insurerAdjudications));
    claimJobItems.push(createJobItem(LineItem.part, 0.0, insurerAdjudications));
    claimJobItems.push(createJobItem(LineItem.other, 0.0, insurerAdjudications));

    claimJob.claimJobItems = claimJobItems;
    claimJobs[claimJobIndex] = claimJob;

    updateClaimJob(claimJob, claimJobIndex);
  }
  const updateJobItem = (
    claimJobIndex: number,
    fieldName: keyof Model.IClaimJobItem,
    value: any,
    targetItem: PublicClaimJobItem
  ) => {
    console.log({ claimJobIndex, fieldName, value, targetItem });
    const claimJobs = form.data?.claim?.claimJobs || [];
    const updatedJobs = [...claimJobs];
    const currentJob = updatedJobs[claimJobIndex];

    currentJob.claimJobItems = currentJob.claimJobItems?.map((item) => {
      if (item !== targetItem) return item;

      const corr = fieldName === 'correction' ? value : (item.correction ?? 0.0);
      const gst = fieldName === 'gstHst' ? value : (item.gstHst ?? 0.0);
      const pst = fieldName === 'provincialSalesTax' ? value : (item.provincialSalesTax ?? 0.0);

      const updatedItem: PublicClaimJobItem = {
        ...item,
        [fieldName]: value,
        correction: corr,
        adjudication: corr,
        total: Number((corr + corr * (gst + pst)).toFixed(2)),
      };

      if (fieldName === 'lossCode' && updatedItem.jobItemTypeCode === LineItem.part)
        updatedItem.quantity = !updatedItem.quantity ? 1 : updatedItem.quantity;

      if (updatedItem.jobItemTypeCode === LineItem.deductible) {
        updatedItem.total = corr;
        updatedItem.gstHst = 0.0;
        updatedItem.provincialSalesTax = 0.0;
      }

      updatedItem.hoursWorked =
        updatedItem.jobItemTypeCode === LineItem.labour && !item.hoursWorked ? 1 : item.hoursWorked;

      if (updatedItem.jobItemTypeCode === LineItem.part && (fieldName === 'baseCost' || fieldName === 'quantity')) {
        const quantity = Number(updatedItem.quantity ?? 0);
        const baseCost = Number(updatedItem.baseCost ?? 0);

        if (quantity === 0) {
          updatedItem.quantity = 0;
          updatedItem.baseCost = 0;
          updatedItem.correction = 0;
          updatedItem.adjudication = 0;
          updatedItem.total = 0;
        } else {
          const correction = baseCost * quantity;
          updatedItem.correction = correction;
          updatedItem.adjudication = correction;
          updatedItem.total = Number((correction + correction * (gst + pst)).toFixed(2));
        }
      }

      return updatedItem;
    });

    updateClaimJob(currentJob, claimJobIndex);
  };

  return {
    form,
    dealerListPublicApi,
    claimTypesListPublicApi,
    repairFacilityListApi,
    isEditingTax,
    setIsEditingTax,
    uploadableDocuments,
    updateClaimJob,
    addNewClaimJob,
    deleteClaimJob,
    updateClaimJobClaimType,
    updateDetail,
    updateJobItem,
    addJobItem,
    deleteJobItem,
    createDefaultJobItems,
    claimTypes: claimTypesListPublicApi?.payload?.data,
    selectedDealer,
    isWarrantyProduct,
    shouldShowClaimJobs,
  };
}

const { useProvider: usePublicClaimSubmitProvider, Provider: PublicClaimSubmitProvider } =
  ProviderBuilder(usePublicClaimSubmit);

export { PublicClaimSubmitProvider, usePublicClaimSubmitProvider };
